import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { User, Save, GraduationCap, Users, Award, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const TABS = [
  { id: 'personal', label: 'Personal Info', icon: User },
  { id: 'family', label: 'Family', icon: Users },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'academic', label: 'Academics & Testing', icon: Award },
];

const CITIZENSHIP_OPTIONS = ["U.S. Citizen", "U.S. Permanent Resident", "International Student", "DACA/Undocumented", "Other"];
const EDUCATION_LEVELS = ["No high school", "High school", "Some college", "Associate's", "Bachelor's", "Master's", "Doctorate / Professional"];
const INCOME_OPTIONS = ["Under $30,000", "$30,000 - $48,000", "$48,000 - $75,000", "$75,000 - $110,000", "$110,000 - $200,000", "Over $200,000", "Prefer not to say"];

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('personal');
  const [form, setForm] = useState({
    first_name: '', last_name: '', date_of_birth: '', citizenship: '',
    address: '', city: '', state: '', zip_code: '',
    parent1_name: '', parent1_education: '', parent1_occupation: '',
    parent2_name: '', parent2_education: '', parent2_occupation: '', household_income: '',
    high_school: '', graduation_year: '', gpa: '', gpa_weighted: '',
    class_rank: '', class_size: '', intended_major: '',
    sat_score: '', act_score: '', sat_math: '', sat_reading: '',
    ap_courses: '', honors_courses: '', ap_scores: '',
  });

  useEffect(() => {
    api.getProfile().then((data) => {
      setForm(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (data[k] != null) next[k] = String(data[k]); });
        return next;
      });
    }).catch(() => toast.error('Failed to load profile')).finally(() => setLoading(false));
    // Run once on mount — form is hydrated from server, no other deps needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ['gpa', 'gpa_weighted'].forEach(k => { payload[k] = payload[k] ? parseFloat(payload[k]) : null; });
      ['graduation_year', 'class_size', 'sat_score', 'act_score', 'sat_math', 'sat_reading'].forEach(k => {
        payload[k] = payload[k] ? parseInt(payload[k]) : null;
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      await api.updateProfile(payload);
      toast.success('Profile saved');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const completedFields = Object.values(form).filter(v => v && v.trim()).length;
  const totalFields = Object.keys(form).length;
  const pct = Math.round((completedFields / totalFields) * 100);

  if (loading) return <div className="space-y-4 animate-pulse"><div className="h-8 bg-[#E2E2E2] rounded w-40" /><div className="h-64 bg-[#E2E2E2] rounded-xl" /></div>;

  const inputCls = "mt-1 border-[#E2E2E2] focus:border-[#0F392B] focus:ring-[#0F392B]";
  const selectCls = "mt-1 w-full px-3 py-2 text-sm border border-[#E2E2E2] rounded-md bg-white focus:outline-none focus:border-[#0F392B]";

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Profile</h1>
          <p className="text-xs text-muted-foreground mt-1">{completedFields}/{totalFields} fields &middot; {pct}% complete</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="save-profile">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>

      <div className="h-1.5 bg-[#F2F2F0] rounded-full overflow-hidden">
        <div className="h-full bg-[#0F392B] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <div className="flex gap-1 bg-[#F9F9F7] p-1 rounded-lg" data-testid="profile-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md transition-all ${tab === t.id ? 'bg-white text-[#0F392B] shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <Card className="border-[#E2E2E2]">
        <CardContent className="p-6">
          {tab === 'personal' && (
            <div className="space-y-5" data-testid="section-personal">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">First Name</Label>
                  <Input className={inputCls} value={form.first_name} onChange={e => handleChange('first_name', e.target.value)} placeholder="Your first name" data-testid="field-first_name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last Name</Label>
                  <Input className={inputCls} value={form.last_name} onChange={e => handleChange('last_name', e.target.value)} placeholder="Your last name" data-testid="field-last_name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Date of Birth</Label>
                  <Input type="date" className={inputCls} value={form.date_of_birth} onChange={e => handleChange('date_of_birth', e.target.value)} data-testid="field-date_of_birth" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Citizenship</Label>
                  <select className={selectCls} value={form.citizenship} onChange={e => handleChange('citizenship', e.target.value)} data-testid="field-citizenship">
                    <option value="">Select...</option>
                    {CITIZENSHIP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Street Address</Label>
                <Input className={inputCls} value={form.address} onChange={e => handleChange('address', e.target.value)} placeholder="123 Main St" data-testid="field-address" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">City</Label>
                  <Input className={inputCls} value={form.city} onChange={e => handleChange('city', e.target.value)} placeholder="City" data-testid="field-city" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Input className={inputCls} value={form.state} onChange={e => handleChange('state', e.target.value)} placeholder="State" data-testid="field-state" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">ZIP Code</Label>
                  <Input className={inputCls} value={form.zip_code} onChange={e => handleChange('zip_code', e.target.value)} placeholder="12345" data-testid="field-zip_code" />
                </div>
              </div>
            </div>
          )}

          {tab === 'family' && (
            <div className="space-y-6" data-testid="section-family">
              <div>
                <p className="text-sm font-medium text-[#0F392B] mb-3">Parent / Guardian 1</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Full Name</Label>
                    <Input className={inputCls} value={form.parent1_name} onChange={e => handleChange('parent1_name', e.target.value)} placeholder="Name" data-testid="field-parent1_name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Education Level</Label>
                    <select className={selectCls} value={form.parent1_education} onChange={e => handleChange('parent1_education', e.target.value)} data-testid="field-parent1_education">
                      <option value="">Select...</option>
                      {EDUCATION_LEVELS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Occupation</Label>
                    <Input className={inputCls} value={form.parent1_occupation} onChange={e => handleChange('parent1_occupation', e.target.value)} placeholder="Occupation" data-testid="field-parent1_occupation" />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-[#0F392B] mb-3">Parent / Guardian 2</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Full Name</Label>
                    <Input className={inputCls} value={form.parent2_name} onChange={e => handleChange('parent2_name', e.target.value)} placeholder="Name" data-testid="field-parent2_name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Education Level</Label>
                    <select className={selectCls} value={form.parent2_education} onChange={e => handleChange('parent2_education', e.target.value)} data-testid="field-parent2_education">
                      <option value="">Select...</option>
                      {EDUCATION_LEVELS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Occupation</Label>
                    <Input className={inputCls} value={form.parent2_occupation} onChange={e => handleChange('parent2_occupation', e.target.value)} placeholder="Occupation" data-testid="field-parent2_occupation" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Approximate Household Income</Label>
                <select className={selectCls} value={form.household_income} onChange={e => handleChange('household_income', e.target.value)} data-testid="field-household_income">
                  <option value="">Select...</option>
                  {INCOME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          )}

          {tab === 'education' && (
            <div className="space-y-5" data-testid="section-education">
              <div>
                <Label className="text-xs text-muted-foreground">High School Name</Label>
                <Input className={inputCls} value={form.high_school} onChange={e => handleChange('high_school', e.target.value)} placeholder="Your high school" data-testid="field-high_school" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Graduation Year</Label>
                  <Input type="number" className={inputCls} value={form.graduation_year} onChange={e => handleChange('graduation_year', e.target.value)} placeholder="2026" data-testid="field-graduation_year" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Class Rank</Label>
                  <Input className={inputCls} value={form.class_rank} onChange={e => handleChange('class_rank', e.target.value)} placeholder="e.g. Top 10%" data-testid="field-class_rank" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Class Size</Label>
                  <Input type="number" className={inputCls} value={form.class_size} onChange={e => handleChange('class_size', e.target.value)} placeholder="e.g. 450" data-testid="field-class_size" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Unweighted GPA</Label>
                  <Input type="number" className={inputCls} value={form.gpa} onChange={e => handleChange('gpa', e.target.value)} placeholder="e.g. 3.85" data-testid="field-gpa" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Weighted GPA</Label>
                  <Input type="number" className={inputCls} value={form.gpa_weighted} onChange={e => handleChange('gpa_weighted', e.target.value)} placeholder="e.g. 4.3" data-testid="field-gpa_weighted" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Intended Major</Label>
                <Input className={inputCls} value={form.intended_major} onChange={e => handleChange('intended_major', e.target.value)} placeholder="e.g. Computer Science" data-testid="field-intended_major" />
              </div>
            </div>
          )}

          {tab === 'academic' && (
            <div className="space-y-5" data-testid="section-academic">
              <p className="text-xs text-muted-foreground">Standardized test scores and coursework</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">SAT Total</Label>
                  <Input type="number" className={inputCls} value={form.sat_score} onChange={e => handleChange('sat_score', e.target.value)} placeholder="e.g. 1520" data-testid="field-sat_score" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">SAT Math</Label>
                  <Input type="number" className={inputCls} value={form.sat_math} onChange={e => handleChange('sat_math', e.target.value)} placeholder="e.g. 780" data-testid="field-sat_math" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">SAT Reading</Label>
                  <Input type="number" className={inputCls} value={form.sat_reading} onChange={e => handleChange('sat_reading', e.target.value)} placeholder="e.g. 740" data-testid="field-sat_reading" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">ACT Composite</Label>
                <Input type="number" className={inputCls} value={form.act_score} onChange={e => handleChange('act_score', e.target.value)} placeholder="e.g. 34" data-testid="field-act_score" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">AP Courses Taken (comma-separated)</Label>
                <Input className={inputCls} value={form.ap_courses} onChange={e => handleChange('ap_courses', e.target.value)} placeholder="e.g. AP Calculus BC, AP Physics C, AP English Language" data-testid="field-ap_courses" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Honors Courses (comma-separated)</Label>
                <Input className={inputCls} value={form.honors_courses} onChange={e => handleChange('honors_courses', e.target.value)} placeholder="e.g. Honors Chemistry, Honors Pre-Calc" data-testid="field-honors_courses" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">AP Exam Scores (comma-separated)</Label>
                <Input className={inputCls} value={form.ap_scores} onChange={e => handleChange('ap_scores', e.target.value)} placeholder="e.g. Calc BC: 5, Physics C: 4, English Lang: 5" data-testid="field-ap_scores" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">Auto-saved when you click Save. All data is encrypted and private.</p>
    </div>
  );
}
